import { BusinessPartnerBySubRoute } from '../interfaces/busines-partner-by-sub-route.interface';

/**
 * Alcance de visitas de preventa: qué clientes se visitan cada día.
 *
 * El vendedor revisa su carga día por día antes de planificar; una lista plana
 * de 60 clientes no permite esa lectura. Estas funciones derivan los días de
 * visita de cada cliente (de sus frecuencias semanales) y arman las secciones.
 *
 * Módulo puro: entran clientes, salen listas. Sin estado ni HTTP.
 */

/** Etiqueta corta de cada día (1 = lunes … 7 = domingo). */
export const DAY_LABELS: Readonly<Record<number, string>> = {
  1: 'Lun',
  2: 'Mar',
  3: 'Mié',
  4: 'Jue',
  5: 'Vie',
  6: 'Sáb',
  7: 'Dom',
};

/** Una sección del alcance: un día (o los que no tienen ninguno) y sus clientes. */
export interface VisitDayGroup {
  /** Código del día (1..7), o `null` para los clientes sin día elegible. */
  code: number | null;
  label: string;
  clients: BusinessPartnerBySubRoute[];
}

/** Etiqueta de la sección de los clientes que no tienen ningún día de visita. */
export const NO_DAY_LABEL = 'Sin día asignado';

/** Frecuencia de un cliente dentro de la sub-ruta (forma mínima usada acá). */
interface ClientFrequency {
  type?: string;
  days?: number[];
}

/**
 * Días en que se visita a un cliente, ordenados y sin repetir.
 *
 * Solo cuentan las frecuencias **semanales** (`W`): las de tipo `C` se definen
 * por fechas concretas y no aportan un día de la semana.
 */
export function clientVisitDays(client: BusinessPartnerBySubRoute): number[] {
  const freqs = (client?.frequenciesFromThisSubRoute ?? []) as unknown as ClientFrequency[];
  const days = new Set<number>();
  for (const freq of freqs) {
    if (freq?.type !== 'W') continue;
    for (const day of freq.days ?? []) days.add(day);
  }
  return [...days].sort((a, b) => a - b);
}

/**
 * Clientes que entran en el alcance del día elegido.
 *
 * `day = 0` es "Todos": no filtra. Con un día concreto, deja los que tienen
 * visita ese día.
 */
export function scopeClientsByDay(
  clients: BusinessPartnerBySubRoute[],
  day: number,
): BusinessPartnerBySubRoute[] {
  if (!day) return clients ?? [];
  return (clients ?? []).filter((c) => clientVisitDays(c).includes(day));
}

/**
 * Agrupa el alcance en secciones por día de visita.
 *
 * Un cliente de varios días aparece **en cada uno** —se lo visita en todos—, así
 * que la suma de las secciones puede superar el total de clientes. Quien
 * necesite el total real cuenta los clientes, no las filas.
 *
 * Solo se crean secciones para días que tienen clientes, en orden lunes→domingo.
 * Los clientes sin ningún día elegible van a una sección propia al final: es un
 * dato faltante que el usuario tiene que ver, no algo para ocultar.
 */
export function groupClientsByDay(clients: BusinessPartnerBySubRoute[]): VisitDayGroup[] {
  const byDay = new Map<number, BusinessPartnerBySubRoute[]>();
  const withoutDay: BusinessPartnerBySubRoute[] = [];

  for (const client of clients ?? []) {
    const days = clientVisitDays(client);
    if (!days.length) {
      withoutDay.push(client);
      continue;
    }
    for (const day of days) {
      const list = byDay.get(day);
      if (list) list.push(client);
      else byDay.set(day, [client]);
    }
  }

  const groups: VisitDayGroup[] = [...byDay.entries()]
    .sort(([a], [b]) => a - b)
    .map(([code, list]) => ({ code, label: DAY_LABELS[code] ?? String(code), clients: list }));

  if (withoutDay.length) {
    groups.push({ code: null, label: NO_DAY_LABEL, clients: withoutDay });
  }
  return groups;
}
